import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { MapPin, Settings, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

/**
 * LocationPermissionBanner - Shows when location permission is denied or not granted
 * Provides clear instructions on how to enable location for PWA
 */
const LocationPermissionBanner = ({ onRetry, compact = false }) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [permissionState, setPermissionState] = useState('prompt'); // 'granted', 'denied', 'prompt'
  
  useEffect(() => {
    checkPermission();
  }, []);
  
  // Auto-expand instructions when permission is denied
  useEffect(() => {
    if (permissionState === 'denied') {
      setShowInstructions(true);
    }
  }, [permissionState]);
  
  const checkPermission = async () => {
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionState(result.state);
        
        result.onchange = () => {
          setPermissionState(result.state);
          if (result.state === 'granted' && onRetry) {
            onRetry();
          }
        };
      } catch (e) {
        // Permissions API not supported
        console.log('Permissions API not supported');
      }
    }
  };
  
  const handleRequestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPermissionState('granted');
          if (onRetry) onRetry();
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setPermissionState('denied');
            setShowInstructions(true);
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };
  
  // Don't show if permission is granted
  if (permissionState === 'granted') {
    return null;
  }
  
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isChrome = /Chrome/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !isChrome;
  
  const getInstructions = () => {
    if (isIOS && isSafari) {
      return (
        <div className="space-y-2 text-sm">
          <p className="font-medium">To enable location on iOS:</p>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>Open <strong>Settings</strong> app on your iPhone/iPad</li>
            <li>Go to <strong>Privacy & Security</strong> → <strong>Location Services</strong></li>
            <li>Make sure <strong>Location Services</strong> is ON</li>
            <li>Scroll down and tap <strong>Safari Websites</strong></li>
            <li>Select <strong>While Using</strong> or <strong>Ask Next Time</strong></li>
            <li>Come back to this app and tap the button below</li>
          </ol>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 font-medium">
            📱 If using "Add to Home Screen" app: Also check Settings → BizilGO → Location
          </p>
        </div>
      );
    }
    
    if (isAndroid && isChrome) {
      return (
        <div className="space-y-2 text-sm">
          <p className="font-medium">To enable location on Android:</p>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>Tap the <strong>lock icon</strong> (🔒) in the address bar</li>
            <li>Tap <strong>Permissions</strong></li>
            <li>Find <strong>Location</strong> and select <strong>Allow</strong></li>
            <li>Tap the button below to try again</li>
          </ol>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 font-medium">
            📱 Also ensure: Phone Settings → Location → ON
          </p>
        </div>
      );
    }
    
    if (isChrome) {
      return (
        <div className="space-y-2 text-sm">
          <p className="font-medium">To enable location in Chrome:</p>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>Click the <strong>lock icon</strong> (🔒) in the address bar</li>
            <li>Click <strong>Site settings</strong></li>
            <li>Find <strong>Location</strong> and change to <strong>Allow</strong></li>
            <li>Refresh the page or tap the button below</li>
          </ol>
        </div>
      );
    }
    
    // Generic instructions
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">To enable location:</p>
        <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
          <li>Look for a <strong>lock icon</strong> (🔒) in your browser's address bar</li>
          <li>Tap it and find <strong>Location</strong> or <strong>Site permissions</strong></li>
          <li>Change the setting to <strong>Allow</strong></li>
          <li>Tap the button below to try again</li>
        </ol>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 font-medium">
          📱 Also ensure location is enabled in your device settings
        </p>
      </div>
    );
  };
  
  if (compact) {
    return (
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {permissionState === 'denied' ? 'Location blocked' : 'Location access needed'}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {permissionState === 'denied' 
                ? 'Tap lock icon in address bar → Allow location' 
                : 'Enable location to set pickup automatically'}
            </p>
          </div>
          {permissionState === 'denied' ? (
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={handleRequestLocation}
            >
              Enable
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-amber-800 dark:text-amber-200">
            {permissionState === 'denied' ? 'Location Access Blocked' : 'Enable Location Access'}
          </h3>
          <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
            {permissionState === 'denied' 
              ? 'You blocked location access. Follow the steps below to enable it in your browser settings.'
              : 'Allow location access to automatically set your pickup point and find nearby drivers faster.'
            }
          </p>
        </div>
      </div>
      
      {permissionState === 'denied' && (
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-800 font-medium"
        >
          <Settings className="w-4 h-4" />
          {showInstructions ? 'Hide instructions' : 'Show how to enable'}
          {showInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      )}
      
      {showInstructions && (
        <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
          {getInstructions()}
        </div>
      )}
      
      <div className="flex gap-2">
        <Button 
          onClick={handleRequestLocation}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
        >
          <MapPin className="w-4 h-4 mr-2" />
          {permissionState === 'denied' ? 'Try Again' : 'Enable Location'}
        </Button>
        {permissionState === 'denied' && (
          <Button
            variant="outline"
            className="border-amber-300 text-amber-700"
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        )}
      </div>
    </div>
  );
};

export default LocationPermissionBanner;
